<script lang="ts" setup generic="T extends { id: string }">
import { computed, ref, watch } from 'vue';
import type { DrawerProps } from 'naive-ui';
import type { BasicDrawerFormProps, BasicFormExpose } from '@/typings/basic-form';

/** 自定义抽屉表单组件 */
defineOptions({
  name: 'BasicDrawerForm'
});

const basicFormRef = ref<BasicFormExpose<T> | null>(null);

const props = withDefaults(defineProps<BasicDrawerFormProps<T>>(), {
  modalConfig: () => {
    return {};
  },
  operation: 'view',
  showDrawerActions: true,
  showMessageOnSuccess: true,
  showFooter: true,
  formConfig: () => {
    return {};
  }
});
const emit = defineEmits<{
  save: [Partial<T>];
  update: [Partial<T>];
  close: [];
}>();
/** 显示状态 */
const show = defineModel('show', { type: Boolean, default: false, required: true });
const visible = defineModel('visible', { type: Boolean, default: false, required: false });

watch(
  visible,
  val => {
    show.value = val;
  },
  {
    immediate: true
  }
);

/** 弹窗配置 */
const finalModalConfig = computed<DrawerProps>(() => {
  const { modalConfig } = props;
  return {
    blockScroll: true,
    closeOnEsc: props.operation === 'view',
    maskClosable: props.operation === 'view',
    defaultWidth: 745,
    minWidth: 500,
    resizable: true,
    ...modalConfig
  };
});

/** 标题 */
const finalTitle = computed<string>(() => {
  if (props.customTitle) {
    return props.customTitle;
  }
  if (props.title === undefined) {
    return '';
  }
  let title = '';
  if (typeof props.title === 'function') {
    title = props.title({ data: basicFormRef.value?.model ?? undefined, operation: props.operation });
  } else {
    title = props.title as string;
  }
  switch (props.operation) {
    case 'add':
      return `新增${title}`;
    case 'edit':
      return `编辑${title}`;
    case 'view':
      return `查看${title}`;
    default:
      return title;
  }
});

async function handleSubmit() {
  if (basicFormRef.value === null || basicFormRef.value.model === null) {
    return;
  }
  if (basicFormRef.value.model === null) {
    return;
  }
  try {
    await basicFormRef.value.validate();
    switch (props.operation) {
      case 'add':
        if (props.saveFn) {
          try {
            basicFormRef.value.action = 'submit';
            basicFormRef.value.startLoading();
            const submitModel = basicFormRef.value.getSubmitModel();
            const result = await props.saveFn?.(submitModel);
            // eslint-disable-next-line max-depth
            if (result?.error === null) {
              // eslint-disable-next-line max-depth
              if (props.showMessageOnSuccess) {
                window.$message?.success('保存成功');
              }
              emit('save', basicFormRef.value.model);
              closeDrawer();
            }
          } finally {
            basicFormRef.value.endLoading();
            basicFormRef.value.action = null;
          }
        } else {
          emit('save', basicFormRef.value.model);
        }
        break;
      case 'edit':
        if (props.updateFn) {
          try {
            basicFormRef.value.action = 'submit';
            basicFormRef.value.startLoading();
            const submitModel = basicFormRef.value.getSubmitModel();
            const result = await props.updateFn?.(submitModel as { id: string; [key: string]: any });
            // eslint-disable-next-line max-depth
            if (result?.error === null) {
              // eslint-disable-next-line max-depth
              if (props.showMessageOnSuccess) {
                window.$message?.success('更新成功');
              }
              emit('update', basicFormRef.value.model);
              closeDrawer();
            }
          } finally {
            basicFormRef.value.endLoading();
            basicFormRef.value.action = null;
          }
        } else {
          emit('update', basicFormRef.value.model);
        }
        break;
      default:
        break;
    }
  } catch {
    // window.$message?.error(error);
  }
}

function closeDrawer() {
  show.value = false;
}

watch(show, val => {
  if (!val) {
    emit('close');
  }
});

defineExpose({
  show,
  basicFormRef,
  model: computed(() => basicFormRef.value?.model),
  validate: computed(() => basicFormRef.value?.validate),
  restoreValidation: computed(() => basicFormRef.value?.restoreValidation),
  closeDrawer,
  handleSubmit
});
</script>

<template>
  <NDrawer v-model:show="show" v-bind="finalModalConfig">
    <NDrawerContent closable :title="finalTitle">
      <BasicForm ref="basicFormRef" v-model:show="show" v-bind="props" :show-title="false">
        <template v-for="(_, name) in $slots" #[name]="data" :key="name">
          <slot v-bind="data" :key="name" :name="name"></slot>
        </template>
      </BasicForm>
      <template
        v-if="showDrawerActions && !basicFormRef?.loading && (operation !== 'view' || $slots.action !== undefined)"
        #footer
      >
        <NSpace v-if="operation !== 'view' && showFooter" :size="16" class="footer-space">
          <NButton @click="closeDrawer">取消</NButton>
          <NButton type="primary" @click="handleSubmit">确认</NButton>
        </NSpace>
        <slot name="action" :operation="operation" :data="basicFormRef?.model"></slot>
      </template>
    </NDrawerContent>
  </NDrawer>
</template>

<style scoped lang="scss"></style>
